import {
  Box,
  IconButton,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { PhotoCamera, PhotoLibrary, Delete } from '@mui/icons-material';
import { useRef, useState, ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton } from '@/shared/components';

interface PhotoUploadProps {
  photos: File[];
  onChange: (photos: File[]) => void;
  maxPhotos?: number;
  maxSizeMB?: number;
  existingPhotos?: { id: string; url: string; thumbnailUrl?: string }[];
  onDeleteExisting?: (id: string) => void;
}

export const PhotoUpload = ({
  photos,
  onChange,
  maxPhotos = 3,
  maxSizeMB = 10,
  existingPhotos = [],
  onDeleteExisting,
}: PhotoUploadProps) => {
  const { t } = useTranslation();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string>('');
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect if mobile device
  useEffect(() => {
    const checkMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    setIsMobile(checkMobile);
  }, []);

  // Detect iOS (WKWebView ignores capture="environment" — fall back to no capture so
  // the system picker offers both camera and gallery)
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    // Reset error
    setError('');

    // Validate count
    if (existingPhotos.length + photos.length + files.length > maxPhotos) {
      setError(t('publish.product.photoUpload.maxPhotosError', { maxPhotos }));
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    for (const file of files) {
      // Validate file type
      if (!/image\/(jpeg|png)/.test(file.type)) {
        setError(t('publish.product.photoUpload.invalidFormatError'));
        continue;
      }

      // Validate file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSizeMB) {
        setError(t('publish.product.photoUpload.fileSizeError', { maxSize: maxSizeMB }));
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      onChange([...photos, ...validFiles]);
    }

    // Reset inputs
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
    if (galleryInputRef.current) {
      galleryInputRef.current.value = '';
    }
  };

  const handleDelete = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onChange(newPhotos);
    setError('');
  };

  const handleAddPhotoClick = () => {
    setBottomSheetOpen(true);
  };

  const handleCloseBottomSheet = () => {
    setBottomSheetOpen(false);
  };

  const handleCameraClick = async () => {
    setBottomSheetOpen(false);

    if (isMobile) {
      // On mobile, use native camera via file input
      cameraInputRef.current?.click();
    } else {
      // On desktop, open camera dialog with MediaDevices API
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        setStream(mediaStream);
        setCameraDialogOpen(true);

        // Wait for video element to be ready
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
          }
        }, 100);
      } catch (err) {
        console.error('Camera access error:', err);
        setError(t('publish.product.photoUpload.cameraAccessError') || 'Unable to access camera');
      }
    }
  };

  const handleGalleryClick = () => {
    setBottomSheetOpen(false);
    galleryInputRef.current?.click();
  };

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0);

      // Convert canvas to blob
      canvas.toBlob(
        blob => {
          if (blob) {
            // Create a File object from the blob
            const file = new File([blob], `photo-${Date.now()}.jpg`, {
              type: 'image/jpeg',
            });

            // Add to photos
            onChange([...photos, file]);
            handleCloseCameraDialog();
          }
        },
        'image/jpeg',
        0.9
      );
    }
  };

  const handleCloseCameraDialog = () => {
    // Stop all video tracks
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraDialogOpen(false);
  };

  return (
    <Box>
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture={isIOS ? undefined : 'environment'}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/jpeg,image/png"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Single Add Photo Button */}
      <AppButton
        variant="outlined"
        fullWidth
        onClick={handleAddPhotoClick}
        disabled={existingPhotos.length + photos.length >= maxPhotos}
        startIcon={<PhotoCamera />}
        sx={{ mb: 2 }}
      >
        {t('publish.product.addPhoto')}
      </AppButton>

      {error && (
        <Typography variant="body2" color="error" mb={2}>
          {error}
        </Typography>
      )}

      {/* Existing (server-side) photo thumbnails */}
      {existingPhotos.length > 0 && (
        <Stack direction="row" spacing={2} flexWrap="wrap" gap={2} mb={photos.length > 0 ? 2 : 0}>
          {existingPhotos.map((photo, index) => (
            <Box
              key={photo.id}
              sx={{
                position: 'relative',
                width: 120,
                height: 120,
                borderRadius: 2,
                overflow: 'hidden',
                border: '2px solid',
                borderColor: 'primary.main',
              }}
            >
              {/* Order badge */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 'bold',
                  zIndex: 1,
                }}
              >
                {index + 1}
              </Box>

              {/* Delete button */}
              {onDeleteExisting && (
                <IconButton
                  size="small"
                  onClick={() => onDeleteExisting(photo.id)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    zIndex: 1,
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}

              <img
                src={photo.thumbnailUrl || photo.url}
                alt={`Preview ${index + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
          ))}
        </Stack>
      )}

      {/* Photo previews */}
      {photos.length > 0 && (
        <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
          {photos.map((photo, index) => (
            <Box
              key={`${photo.name}-${index}`}
              sx={{
                position: 'relative',
                width: 120,
                height: 120,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              {/* Order badge */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 'bold',
                  zIndex: 1,
                }}
              >
                {existingPhotos.length + index + 1}
              </Box>

              {/* Delete button */}
              <IconButton
                size="small"
                onClick={() => handleDelete(index)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  zIndex: 1,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                  },
                }}
              >
                <Delete fontSize="small" />
              </IconButton>

              {/* Preview image */}
              <img
                src={URL.createObjectURL(photo)}
                alt={`Preview ${existingPhotos.length + index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
                onLoad={e => {
                  // Cleanup object URL after image loads
                  URL.revokeObjectURL((e.target as HTMLImageElement).src);
                }}
              />
            </Box>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        {t('publish.product.photoUpload.helperText', { maxSize: maxSizeMB })}
      </Typography>

      {/* Camera Dialog for Desktop */}
      <Dialog open={cameraDialogOpen} onClose={handleCloseCameraDialog} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', bgcolor: 'black' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            >
              <track kind="captions" />
            </video>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={handleCloseCameraDialog} variant="text">
            {t('common.cancel') || 'Cancel'}
          </AppButton>
          <AppButton onClick={handleCapture} variant="primary" startIcon={<PhotoCamera />}>
            {t('common.capture') || 'Capture'}
          </AppButton>
        </DialogActions>
      </Dialog>

      {/* Bottom Sheet for Photo Options */}
      <Drawer
        anchor="bottom"
        open={bottomSheetOpen}
        onClose={handleCloseBottomSheet}
        slotProps={{
          paper: {
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              pb: 2,
            },
          },
        }}
      >
        <Box sx={{ pt: 1, pb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: 'grey.300',
              borderRadius: 2,
              mx: 'auto',
              mb: 2,
            }}
          />
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleCameraClick}>
                <ListItemIcon>
                  <PhotoCamera color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('publish.product.takePhoto')}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: '16px',
                        fontWeight: 500,
                      },
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleGalleryClick}>
                <ListItemIcon>
                  <PhotoLibrary color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('publish.product.chooseFromGallery')}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: '16px',
                        fontWeight: 500,
                      },
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};
